import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiPlus,
  FiEdit2,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './Capacity.module.css';

interface CapacityItem {
  id: string;
  country: string;
  period: string;
  maxOrders: number;
  currentUsage: number;
}

const Capacity: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<CapacityItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<CapacityItem | null>(null);
  const [formCountry, setFormCountry] = useState('FR');
  const [formPeriod, setFormPeriod] = useState('');
  const [formMax, setFormMax] = useState('');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchCapacity();
  }, [navigate]);

  const fetchCapacity = async () => {
    try {
      const res = await api.get('/admin/capacity');
      setItems(res.data.capacities || res.data || []);
    } catch {
      // Demo data
      setItems([
        { id: '1', country: 'FR', period: '2024-01', maxOrders: 100, currentUsage: 67 },
        { id: '2', country: 'US', period: '2024-01', maxOrders: 80, currentUsage: 42 },
        { id: '3', country: 'FR', period: '2024-02', maxOrders: 120, currentUsage: 15 },
        { id: '4', country: 'US', period: '2024-02', maxOrders: 100, currentUsage: 8 },
      ]);
    }
  };

  const handleSave = async () => {
    if (!formPeriod || !formMax) return;
    try {
      if (editItem) {
        await api.put(`/admin/capacity/${editItem.id}`, {
          country: formCountry,
          period: formPeriod,
          maxOrders: Number(formMax),
        });
        toast.success('Capacity updated');
      } else {
        await api.post('/admin/capacity', {
          country: formCountry,
          period: formPeriod,
          maxOrders: Number(formMax),
        });
        toast.success('Capacity added');
      }
      setShowModal(false);
      resetForm();
      fetchCapacity();
    } catch {
      toast.error('Error saving capacity');
    }
  };

  const openEdit = (item: CapacityItem) => {
    setEditItem(item);
    setFormCountry(item.country);
    setFormPeriod(item.period);
    setFormMax(String(item.maxOrders));
    setShowModal(true);
  };

  const openAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setEditItem(null);
    setFormCountry('FR');
    setFormPeriod('');
    setFormMax('');
  };

  const getUsageLevel = (usage: number, max: number) => {
    const pct = (usage / max) * 100;
    if (pct < 50) return 'low';
    if (pct < 80) return 'medium';
    return 'high';
  };

  return (
    <AdminLayout>
      <div className={styles.header}>
        <h1>{t('admin.capacityManagement' as string) || t('admin.capacity')}</h1>
        <button className={styles.addBtn} onClick={openAdd}>
          <FiPlus size={16} />
          {t('admin.addCapacity' as string) || 'Add'}
        </button>
      </div>

        <div className={styles.tableSection}>
          {items.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('admin.country' as string) || 'Country'}</th>
                  <th>{t('admin.period' as string) || 'Period'}</th>
                  <th>{t('admin.maxOrders' as string) || 'Max'}</th>
                  <th>{t('admin.currentUsage' as string) || 'Usage'}</th>
                  <th>{t('admin.actions' as string) || 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const pct = Math.round((item.currentUsage / item.maxOrders) * 100);
                  const level = getUsageLevel(item.currentUsage, item.maxOrders);
                  return (
                    <tr key={item.id}>
                      <td>{item.country === 'FR' ? 'France' : 'USA'}</td>
                      <td>{item.period}</td>
                      <td>{item.maxOrders}</td>
                      <td>
                        <span className={styles.usageText}>
                          {item.currentUsage} / {item.maxOrders} ({pct}%)
                        </span>
                        <div className={styles.progressBar}>
                          <div
                            className={`${styles.progressFill} ${styles[level]}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </td>
                      <td>
                        <button
                          className={styles.actionBtn}
                          onClick={() => openEdit(item)}
                        >
                          <FiEdit2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className={styles.emptyState}>No capacity data</div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className={styles.modal}>
            <div className={styles.modalOverlay} onClick={() => setShowModal(false)} />
            <div className={styles.modalCard}>
              <h2>{editItem ? 'Edit Capacity' : (t('admin.addCapacity' as string) || 'Add Capacity')}</h2>

              <div className={styles.formGroup}>
                <label>{t('admin.country' as string) || 'Country'}</label>
                <select
                  className={styles.select}
                  value={formCountry}
                  onChange={(e) => setFormCountry(e.target.value)}
                >
                  <option value="FR">France</option>
                  <option value="US">United States</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>{t('admin.period' as string) || 'Period'}</label>
                <input
                  type="month"
                  className={styles.input}
                  value={formPeriod}
                  onChange={(e) => setFormPeriod(e.target.value)}
                />
              </div>

              <div className={styles.formGroup}>
                <label>{t('admin.maxOrders' as string) || 'Max Orders'}</label>
                <input
                  type="number"
                  className={styles.input}
                  value={formMax}
                  onChange={(e) => setFormMax(e.target.value)}
                  min={1}
                />
              </div>

              <div className={styles.modalActions}>
                <button
                  className={`${styles.modalBtn} ${styles.secondary}`}
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className={`${styles.modalBtn} ${styles.primary}`}
                  onClick={handleSave}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}
    </AdminLayout>
  );
};

export default Capacity;
